import React, { useEffect, useRef, useState } from 'react';

interface Contender {
  id: string;
  model: string;
  tokens: number;
  status: 'streaming' | 'done' | 'waiting';
  score: number | null;
  latencyMs: number | null;
  output: string;
}

interface SpeedRecord {
  rank: number;
  team: string;
  model: string;
  tokensPerSec: number;
  agents: number;
  goal: string;
  ts: number;
}

interface SweBenchResult {
  suite: string;
  model: string;
  resolved: number;
  total: number;
  pct: number;
  submitted: string;
}

const SPEED_LEADERBOARD: SpeedRecord[] = [
  { rank: 1, team: 'vibetime-internal', model: 'claude-opus-4-7', tokensPerSec: 4820, agents: 8, goal: 'migrate payments API', ts: Date.now() - 2 * 3600 * 1000 },
  { rank: 2, team: 'demo-workspace', model: 'claude-sonnet-4-6', tokensPerSec: 3210, agents: 4, goal: 'add OAuth2 flow', ts: Date.now() - 5 * 3600 * 1000 },
  { rank: 3, team: 'vibetime-internal', model: 'claude-opus-4-7', tokensPerSec: 2960, agents: 6, goal: 'scaffold billing UI', ts: Date.now() - 8 * 3600 * 1000 },
  { rank: 4, team: 'local-only', model: 'ollama/llama3', tokensPerSec: 1840, agents: 2, goal: 'fix test suite', ts: Date.now() - 12 * 3600 * 1000 },
];

const SWE_BENCH: SweBenchResult[] = [
  { suite: 'SWE-bench Verified', model: 'claude-opus-4-7', resolved: 248, total: 500, pct: 49.6, submitted: '2026-04-28' },
  { suite: 'SWE-bench Lite', model: 'claude-sonnet-4-6', resolved: 132, total: 300, pct: 44.0, submitted: '2026-04-21' },
  { suite: 'SWE-bench Pro', model: 'claude-opus-4-7', resolved: 89, total: 200, pct: 44.5, submitted: '2026-04-15' },
];

const RACE_CONTENDERS_INITIAL: Contender[] = [
  {
    id: 'c1',
    model: 'claude-opus-4-7',
    tokens: 0,
    status: 'waiting',
    score: null,
    latencyMs: null,
    output: '',
  },
  {
    id: 'c2',
    model: 'claude-sonnet-4-6',
    tokens: 0,
    status: 'waiting',
    score: null,
    latencyMs: null,
    output: '',
  },
  {
    id: 'c3',
    model: 'ollama/llama3',
    tokens: 0,
    status: 'waiting',
    score: null,
    latencyMs: null,
    output: '',
  },
];

const SAMPLE_OUTPUTS = [
  "```typescript\nconst handler = async (req: Request) => {\n  const token = req.headers.get('Authorization');\n  if (!token) return new Response('Unauthorized', { status: 401 });\n  const user = await verifyJWT(token);\n  return Response.json({ user });\n};\n```",
  "```python\ndef handle_request(request):\n    token = request.headers.get('Authorization')\n    if not token:\n        return {'error': 'Unauthorized'}, 401\n    user = verify_jwt(token)\n    return {'user': user}, 200\n```",
  "```go\nfunc handleRequest(w http.ResponseWriter, r *http.Request) {\n\ttoken := r.Header.Get(\"Authorization\")\n\tif token == \"\" {\n\t\thttp.Error(w, \"Unauthorized\", 401)\n\t\treturn\n\t}\n\tuser := verifyJWT(token)\n\tjson.NewEncoder(w).Encode(user)\n}\n```",
];

function fmtAgo(ts: number): string {
  const secs = (Date.now() - ts) / 1000;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function modelShort(m: string): string {
  return m.replace('claude-', '').replace('-4-7', ' 4.7').replace('-4-6', ' 4.6').replace('-4-5', ' 4.5');
}

interface ArenaViewProps {
  mode: 'beginner' | 'pro';
}

export function ArenaView({ mode }: ArenaViewProps) {
  const [contenders, setContenders] = useState<Contender[]>(RACE_CONTENDERS_INITIAL);
  const [raceActive, setRaceActive] = useState(false);
  const [racePrompt] = useState('Implement a JWT auth middleware in TypeScript');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRace = () => {
    setContenders(
      RACE_CONTENDERS_INITIAL.map((c) => ({ ...c, tokens: 0, status: 'streaming', output: '', score: null, latencyMs: null })),
    );
    setRaceActive(true);
    let tick = 0;
    timerRef.current = setInterval(() => {
      tick += 1;
      setContenders((prev) =>
        prev.map((c, i) => {
          const speed = i === 0 ? 14 : i === 1 ? 11 : 7;
          const newTokens = Math.min(c.tokens + speed + Math.floor(Math.random() * 6), 280 + i * 40);
          const done = newTokens >= 280 + i * 40;
          return {
            ...c,
            tokens: newTokens,
            status: done ? 'done' : 'streaming',
            score: done ? +(0.6 + Math.random() * 0.38).toFixed(2) : null,
            latencyMs: done ? 800 + i * 400 + tick * 10 : null,
            output: done ? (SAMPLE_OUTPUTS[i] ?? '') : '',
          };
        }),
      );
      if (tick >= 28) {
        if (timerRef.current) clearInterval(timerRef.current);
        setRaceActive(false);
      }
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const allDone = contenders.every((c) => c.status === 'done');
  const winner = allDone ? [...contenders].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0] : null;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <section className="panel" style={{ padding: 22 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18,
          }}
        >
          <div className="section-heading" style={{ marginBottom: 0 }}>
            <span className="section-kicker">Race cam</span>
            <strong>Head-to-head model race</strong>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.78rem',
                color: 'var(--muted)',
                maxWidth: 340,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {racePrompt}
            </span>
            <button
              type="button"
              className="primary-button"
              style={{ fontSize: '0.82rem', padding: '10px 16px' }}
              onClick={startRace}
              disabled={raceActive}
            >
              {raceActive ? 'Racing…' : 'Start race'}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14,
          }}
        >
          {contenders.map((c) => {
            const pct = Math.min((c.tokens / 320) * 100, 100);
            const isWinner = winner?.id === c.id;
            return (
              <div
                key={c.id}
                style={{
                  padding: '16px',
                  borderRadius: 16,
                  border: `1px solid ${isWinner ? 'rgba(83,246,196,0.5)' : 'var(--line)'}`,
                  background: isWinner ? 'rgba(83,246,196,0.06)' : 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--text)' }}>
                    {modelShort(c.model)}
                    {isWinner && (
                      <span style={{ marginLeft: 8, color: 'var(--mint)', fontSize: '0.72rem' }}>
                        ✓ winner
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontFamily: 'var(--font-mono)',
                      color:
                        c.status === 'done'
                          ? 'var(--mint)'
                          : c.status === 'streaming'
                            ? 'var(--cyan)'
                            : 'var(--soft)',
                    }}
                  >
                    {c.status}
                  </span>
                </div>

                <div
                  style={{
                    height: 8,
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.06)',
                    overflow: 'hidden',
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      borderRadius: 4,
                      background:
                        c.status === 'done'
                          ? 'linear-gradient(90deg, var(--mint), var(--cyan))'
                          : 'linear-gradient(90deg, var(--cyan), var(--lavender))',
                      transition: 'width 0.12s ease',
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.76rem',
                    color: 'var(--muted)',
                    marginBottom: 10,
                  }}
                >
                  <span>{c.tokens} tok</span>
                  {c.latencyMs !== null && <span>{c.latencyMs}ms</span>}
                  {c.score !== null && (
                    <span style={{ color: 'var(--gold)' }}>score {c.score}</span>
                  )}
                </div>

                {c.output && (
                  <pre
                    style={{
                      margin: 0,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(0,0,0,0.3)',
                      fontSize: '0.68rem',
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--muted)',
                      maxHeight: 120,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {c.output.slice(0, 280)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
          gap: 18,
          alignItems: 'start',
        }}
      >
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Speedboard</span>
            <strong>Token throughput leaderboard</strong>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {SPEED_LEADERBOARD.map((row) => (
              <div
                key={row.rank}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: row.rank === 1 ? 'rgba(83,246,196,0.04)' : 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background:
                      row.rank === 1
                        ? 'linear-gradient(135deg, var(--gold), var(--coral))'
                        : 'rgba(255,255,255,0.06)',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: row.rank === 1 ? '#07111a' : 'var(--muted)',
                    flexShrink: 0,
                  }}
                >
                  {row.rank}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text)', marginBottom: 2 }}>
                    {modelShort(row.model)}
                    <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: '0.76rem' }}>
                      {row.agents} agents
                    </span>
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.goal}
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.88rem',
                    color: 'var(--cyan)',
                    flexShrink: 0,
                    textAlign: 'right',
                  }}
                >
                  {row.tokensPerSec.toLocaleString()}
                  <div style={{ fontSize: '0.66rem', color: 'var(--soft)' }}>tok/s</div>
                </div>
              </div>
            ))}
          </div>
          {mode === 'pro' && (
            <button type="button" className="ghost-button" style={{ marginTop: 14, width: '100%', fontSize: '0.8rem' }}>
              Submit new record
            </button>
          )}
        </section>

        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">SWE-bench Pro</span>
            <strong>Benchmark results</strong>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {SWE_BENCH.map((r) => (
              <div
                key={`${r.suite}-${r.model}`}
                style={{
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.84rem', color: 'var(--text)', marginBottom: 2 }}>
                      {r.suite}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>
                      {modelShort(r.model)} · {r.submitted}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '1.2rem',
                      color: r.pct >= 45 ? 'var(--mint)' : 'var(--gold)',
                    }}
                  >
                    {r.pct.toFixed(1)}%
                  </div>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.06)',
                    overflow: 'hidden',
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: `${r.pct}%`,
                      height: '100%',
                      borderRadius: 3,
                      background:
                        r.pct >= 45
                          ? 'linear-gradient(90deg, var(--mint), var(--cyan))'
                          : 'linear-gradient(90deg, var(--gold), var(--coral))',
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--soft)' }}>
                  {r.resolved} / {r.total} resolved
                </div>
              </div>
            ))}
          </div>
          {mode === 'pro' && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Run suite
              </button>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Publish results
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
